import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { AppTriggerEnum } from '@chaine/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_ORBCOMM_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableOrbcommAppInput = {
  appType: AppTypeEnum
  enableAfterCreation: boolean
  isCronBasedOrEventBased: AppTriggerEnum
  source: string
}

const formSchema = z.object({
  source: z.string().min(1, 'Source is required')
})

/**
 * This is the modal for the Orbcomm app.
 */
export function OrbcommModal({ isOpen, onClose, appType }: AppModalProps) {
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      source: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableOrbcommApp, { loading }] = useMutation<
    { enableOrbcommApp: { enableAfterCreation: boolean; isCronBasedOrEventBased: AppTriggerEnum; source: string } },
    EnableOrbcommAppInput
  >(ENABLE_ORBCOMM_APP, {
    onCompleted: () => {
      toast.success('Orbcomm app is enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'Incorrect source'
          ? 'Invalid source. Please try again.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableOrbcommApp({
      variables: {
        appType: appType as AppTypeEnum,
        enableAfterCreation: true,
        isCronBasedOrEventBased: AppTriggerEnum.Cron,
        source: values.source.toLowerCase() === 'orbcomm' ? 'Orbcomm' : values.source
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable Orbcomm for Chain</DialogTitle>
          <DialogDescription>Enabling this integration requires the source as Orbcomm.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Orbcomm source</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter the source as Orbcomm" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://handbook.chaineapp.com/product-guide/apps/orbcomm"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
